/**
 * BailPaidToast.tsx — R3 Bug 1
 *
 * Transient 2s toast shown after a jailed player successfully pays bail.
 * "Paid {amount}T bail — leaving jail."
 * Reuses CardDrawnToast pattern (store-driven, auto-dismiss, click-to-dismiss).
 *
 * VISIBLE WHEN: bailPaidToast !== null
 * TEST PATH: jailed seat pays bail → toast appears → auto-hides ~2s
 */

import type React from "react";
import { useEffect } from "react";
import { useI18n } from "../../i18n/useI18n.js";
import { useGameStore } from "../../store/game-store.js";
import styles from "./BailPaidToast.module.css";

const TOAST_DURATION_MS = 2000;

export function BailPaidToast(): React.ReactElement | null {
  const { t } = useI18n();
  const bailPaidToast = useGameStore((s) => s.bailPaidToast);
  const dismissBailPaidToast = useGameStore((s) => s.dismissBailPaidToast);

  useEffect(() => {
    if (bailPaidToast === null) return;
    const timer = window.setTimeout(() => {
      dismissBailPaidToast();
    }, TOAST_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [bailPaidToast, dismissBailPaidToast]);

  if (bailPaidToast === null) return null;

  return (
    <button
      type="button"
      className={styles.toast}
      onClick={dismissBailPaidToast}
      aria-label="Dismiss bail paid notification"
      data-testid="bail-paid-toast"
    >
      <div className={styles.title}>{t("toast.bailPaid.title")}</div>
      <div className={styles.message}>
        {t("toast.bailPaid.message", { amount: bailPaidToast.amount })}
      </div>
    </button>
  );
}
